import { useState, useEffect, useCallback, useRef } from 'react'

export interface PaginationInfo {
  currentPage: number
  hasNextPage: boolean
  hasPrevPage: boolean
  limit: number
  total: number
  totalPages: number
}

export interface UseInfiniteScrollOptions<T> {
  fetchData: (params: { page: number; limit: number; searchText?: string }) => Promise<{
    data: T[]
    pagination: PaginationInfo
  }>
  limit?: number
  initialSearchText?: string
}

export interface UseInfiniteScrollReturn<T> {
  items: T[]
  loading: boolean
  hasMore: boolean
  searchText: string
  setSearchText: (text: string) => void
  loadMore: () => void
  refresh: () => void
  menuIsOpen: boolean
  setMenuIsOpen: (open: boolean) => void
}

export function useInfiniteScroll<T>({
  fetchData,
  limit = 10,
  initialSearchText = '',
}: UseInfiniteScrollOptions<T>): UseInfiniteScrollReturn<T> {
  const [items, setItems] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchText, setSearchText] = useState(initialSearchText)
  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const searchTimeoutRef = useRef<NodeJS.Timeout>()
  const loadingRef = useRef(false)
  const initialLoadDoneRef = useRef(false)
  const isFirstRender = useRef(true)
  const lastLoadMoreTime = useRef(0)

  const loadData = useCallback(async (page: number, search: string, reset = false) => {
    if (loadingRef.current) {
      console.log('Already loading, skipping...')
      return
    }
    
    console.log('Loading data:', { page, search, reset, currentItems: items.length })
    
    loadingRef.current = true
    setLoading(true)
    try {
      const response = await fetchData({
        page,
        limit,
        searchText: search || undefined,
      })

      console.log('Data response:', { 
        dataLength: response.data.length, 
        hasNextPage: response.pagination.hasNextPage,
        currentPage: response.pagination.currentPage,
        total: response.pagination.total 
      })

      if (reset) {
        setItems(response.data)
      } else {
        setItems(prev => [...prev, ...response.data])
      }
      
      setHasMore(response.pagination.hasNextPage)
      setCurrentPage(page)
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }, [fetchData, limit]) // Removed items.length dependency

  const loadMore = useCallback(() => {
    const now = Date.now()
    const timeSinceLastLoad = now - lastLoadMoreTime.current
    
    console.log('LoadMore called:', { 
      hasMore, 
      loading: loadingRef.current, 
      currentPage, 
      searchText,
      timeSinceLastLoad 
    })
    
    // Throttle loadMore calls - at least 500ms between calls
    if (timeSinceLastLoad < 500) {
      console.log('Throttling loadMore call')
      return
    }
    
    if (hasMore && !loadingRef.current) {
      lastLoadMoreTime.current = now
      loadData(currentPage + 1, searchText, false)
    }
  }, [hasMore, currentPage, searchText, loadData])

  const refresh = useCallback(() => {
    setCurrentPage(1)
    setHasMore(true)
    initialLoadDoneRef.current = false
    loadData(1, searchText, true)
  }, [searchText, loadData])

  const handleSetMenuIsOpen = useCallback((open: boolean) => {
    setMenuIsOpen(open)
  }, [])

  // Handle search text changes with debouncing
  useEffect(() => {
    // Skip search effect on first render or if menu is not open
    if (isFirstRender.current || !menuIsOpen) {
      isFirstRender.current = false
      return
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1)
      setHasMore(true)
      initialLoadDoneRef.current = false
      loadData(1, searchText, true)
    }, 300) // 300ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchText, loadData, menuIsOpen])

  // Initial data load when menu opens
  useEffect(() => {
    if (menuIsOpen && !initialLoadDoneRef.current && !loadingRef.current) {
      initialLoadDoneRef.current = true
      loadData(1, searchText, true)
    }
    
    // Reset initial load flag when menu closes
    if (!menuIsOpen) {
      initialLoadDoneRef.current = false
      setItems([])
      setCurrentPage(1)
      setHasMore(true)
    }
  }, [menuIsOpen, loadData, searchText])

  return {
    items,
    loading,
    hasMore,
    searchText,
    setSearchText,
    loadMore,
    refresh,
    menuIsOpen,
    setMenuIsOpen: handleSetMenuIsOpen,
  }
}